import { useCallback, useMemo } from 'react';
import { useTracking } from 'react-tracking';
import { throttle } from 'lodash';
import { RouterLink } from '@redux-first-router-link';

import { Text, Grid } from '@simplywallst/ui-core';

import SetFairValue from '../../../../FairValue/SetFairValue';
import { TableRowCell } from '../../Table';
import { MoreOptions } from '../../MoreOptions';

import type { Watchlist, WatchlistItem } from '@/hooks/useWatchlist';
import PositiveNegativeValue from '@/components/PositiveNegativeValue';
import MiniPriceChart, { DeferRender } from '@/components/MiniPriceChart';
import { CompanyIntrinsicDiscount } from '@/components/CompanyIntrinsicDiscount';

import { formatterCurrency, formatterPercentage } from '@/utilities/format';

import { useCompanyPeriodReturn } from '../../../useCompanyPeriodReturn';

import { Chart, WatchlistTableRow } from './styled';

type Props = {
  index: number;
  watchlistId: Watchlist['id'];
  watchlistItem: WatchlistItem;
  onWatchlistItemSelect: (id: WatchlistItem['id'] | null) => void;
  'data-cy-id'?: string;
};

const WatchlistRowDesktop = ({
  index,
  watchlistId,
  watchlistItem,
  onWatchlistItemSelect,
  'data-cy-id': dataCyId,
}: Props) => {
  const { id: watchlistItemId, company, fairValue } = watchlistItem;
  const {
    tickerSymbol,
    id: companyId,
    name: companyName,
    canonicalURL,
  } = company;
  const { trading_item_currency_iso: companyCurrencyISO } =
    company.data.currencyInfo;
  const { return7d: companyReturn7d, lastSharePrice: companySharePrice } =
    company.analysisValue;

  const { period, return: companyReturn } = useCompanyPeriodReturn(company);

  const { Track, trackEvent } = useTracking({
    modifier: 'desktopRow',
  });

  const [handleMouseEnter, handleMouseLeave] = useMemo(() => {
    const throttleHover = throttle(() => {
      onWatchlistItemSelect(watchlistItemId);
      trackEvent({
        index,
        action: 'hover',
        canonicalURL,
      });
    }, 300);

    const cancelThrottleHover = () => {
      throttleHover.cancel();
    };
    return [throttleHover, cancelThrottleHover];
  }, [onWatchlistItemSelect, watchlistItemId, canonicalURL, trackEvent, index]);

  const handleRouterLinkClick = useCallback(() => {
    trackEvent({
      index,
      action: 'click',
      canonicalURL,
    });
  }, [trackEvent, canonicalURL, index]);

  const handleClick = useCallback(() => {
    onWatchlistItemSelect(null);
    trackEvent({
      action: 'click',
    });
  }, [onWatchlistItemSelect, trackEvent]);

  return (
    <Track>
      <WatchlistTableRow
        data-cy-id={dataCyId}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        tabIndex={0}
      >
        <TableRowCell>
          <Grid
            data-cy-id="company-column"
            alignItems="center"
            onClick={handleClick}
          >
            <RouterLink
              to={canonicalURL}
              noUnderline
              onClick={handleRouterLinkClick}
            >
              <Text
                data-cy-id="ticker-symbol"
                typography="xSmallStrong"
                color="brand01"
                textTransform="uppercase"
              >
                <strong>{tickerSymbol}</strong>
              </Text>
            </RouterLink>
            <Text
              typography="xSmall"
              color="whiteRGBA"
              opacity={0.5}
              lineClamp={1}
            >
              {companyName}
            </Text>
          </Grid>
        </TableRowCell>
        <TableRowCell data-cy-id="share-price">
          <Grid alignItems="center" onClick={handleClick}>
            <Text typography="xSmall" isTruncated>
              {formatterCurrency(companySharePrice, companyCurrencyISO)}
            </Text>
            <Text typography="tinyCondensed" color="whiteRGBA" opacity={0.5}>
              <CompanyIntrinsicDiscount
                fairValue={fairValue}
                company={company}
              />
            </Text>
          </Grid>
        </TableRowCell>
        <TableRowCell>
          <SetFairValue watchlistItem={watchlistItem} />
        </TableRowCell>
        <TableRowCell>
          <Grid alignItems="center" justifyContent="end" onClick={handleClick}>
            <PositiveNegativeValue value={companyReturn7d}>
              {formatterPercentage(companyReturn7d)}
            </PositiveNegativeValue>
          </Grid>
        </TableRowCell>
        <TableRowCell data-cy-id="period-return">
          <Grid alignItems="center" justifyContent="end" onClick={handleClick}>
            <PositiveNegativeValue value={companyReturn}>
              {formatterPercentage(companyReturn)}
            </PositiveNegativeValue>
          </Grid>
        </TableRowCell>
        <TableRowCell colSpan={2}>
          <Chart>
            <DeferRender>
              <MiniPriceChart
                companyId={companyId}
                period={period}
                currencyISO={companyCurrencyISO}
              />
            </DeferRender>
          </Chart>
        </TableRowCell>
        <TableRowCell>
          <Grid alignItems="center">
            <MoreOptions
              watchlistId={watchlistId}
              watchlistItem={watchlistItem}
            />
          </Grid>
        </TableRowCell>
      </WatchlistTableRow>
    </Track>
  );
};

export default WatchlistRowDesktop;
